/**
 * ExploreFeatures component
 *
 * - Provides a UI for exploring product features, including charts and data tables.
 * - Accepts productName, tenantId, and productFamily as props.
 * - Uses custom hooks to fetch feature data and prepare chart data.
 * - Manages state for showing the data table and chart height.
 * - Handles loading, error, and empty states with dedicated components.
 * - Special handling for "Microsoft Intune":
 *   - Renders a chart for each feature and disables the data table toggle.
 *   - Flattens all feature data for controls.
 * - For other products:
 *   - Renders a single chart and data table for the selected features.
 * - Uses a ButtonWithPanel to open the feature explorer panel and trigger data fetch.
 * - All UI is wrapped in a Fluent UI Stack for layout.
 */
import { Stack } from "@fluentui/react";
import { FormEvent, MouseEvent, useCallback, useMemo, useState } from "react";
import { logEvent, logException } from "../../../helper/AppInsightsHelper";
import ButtonWithPanel from "../../../SharedComponents/ButtonWithPanel";
import { DEFAULT_CHART_HEIGHT } from "../CustomerView.constants";
import { FeatureChart } from "./Components/FeatureChart";
import { FeatureControls } from "./Components/FeatureControls";
import { FeatureDataTable } from "./Components/FeaturesDataTable";
import { EmptyState, ErrorState, LoadingState, NoFeatureSelectedState } from "./Components/FeatureStateMessages";
import { ExploreFeaturesProps } from "./ExploreFeatures.interfaces";
import { useChartData } from "./Hooks/useChartData.hook";
import { useFeatureData } from "./Hooks/useFeatureData.hook";
import { useSecurityCopilotFeatureData } from "./Hooks/useSecurityCopilotFeatureData.hook";

const ExploreFeatures = ({ productName, tenantId, productFamily }: ExploreFeaturesProps) => {
  const [showDataTable, setShowDataTable] = useState(true);
  const [chartHeight, setChartHeight] = useState(DEFAULT_CHART_HEIGHT);

  const isSecurityCopilot = productFamily === "Security Copilot";

  // Use the appropriate hook based on product family
  // For Security Copilot panel (ProductTelemetryChart), show only Monthly data
  const securityCopilotData = useSecurityCopilotFeatureData(isSecurityCopilot ? tenantId : undefined, ["Monthly"]);
  const standardData = useFeatureData(tenantId, productFamily, productName);

  const { rawFeaturesData, selectedFeatures, setSelectedFeatures, isLoading, error, triggerFetch } = isSecurityCopilot
    ? securityCopilotData
    : standardData;

  const selectedFeaturesChartData = useChartData(rawFeaturesData, selectedFeatures);

  const isIntune = productName === "Microsoft Intune";
  const hasNoData = rawFeaturesData.length === 0;

  // Memoize event payloads to avoid unnecessary object creation
  const telemetryPayload = useMemo(
    () => ({
      productName,
      tenantId,
      productFamily
    }),
    [productName, tenantId, productFamily]
  );

  const handleChartHeightChange = useCallback(
    (_event: FormEvent<HTMLDivElement>, option?: { key: number }) => {
      if (option) {
        setChartHeight(option.key);
        logEvent("Product Telemetry", "Customer Page", "ExploreFeatures", {
          ...telemetryPayload,
          eventName: "ChartHeightChanged",
          chartHeight: option.key
        });
      }
    },
    [telemetryPayload]
  );

  const handleDataTableToggle = useCallback(
    (_event?: MouseEvent<HTMLElement>, checked?: boolean) => {
      setShowDataTable(!!checked);
      logEvent("Product Telemetry", "Customer Page", "ExploreFeatures", {
        ...telemetryPayload,
        eventName: "DataTableToggle",
        showDataTable: !!checked
      });
    },
    [telemetryPayload]
  );

  const handlePanelOpen = useCallback(() => {
    triggerFetch();
    logEvent("Product Telemetry", "Customer Page", "ExploreFeatures", {
      ...telemetryPayload,
      eventName: "PanelOpened"
    });
  }, [triggerFetch, telemetryPayload]);

  // Extracted content rendering logic for clarity and performance
  const renderIntuneContent = useCallback(
    () => (
      <>
        <FeatureControls
          chartHeight={chartHeight}
          disableDataTableToggle
          onChartHeightChange={handleChartHeightChange}
          onDataTableToggle={() => {}}
          rawFeatureData={rawFeaturesData.flatMap((feature) => feature.data || [])}
          selectedFeatures={selectedFeatures}
          setSelectedFeatures={setSelectedFeatures}
          showDataTable={false}
        />
        {selectedFeaturesChartData.length === 0 && <NoFeatureSelectedState />}
        {selectedFeaturesChartData.map((chartData, idx) => (
          <FeatureChart
            key={`feature-chart-${idx}`}
            chartTitle={rawFeaturesData[idx]?.name}
            chartData={chartData}
            selectedFeatures={selectedFeatures}
            chartHeight={chartHeight}
            showDataTable={showDataTable}
          />
        ))}
      </>
    ),
    [
      chartHeight,
      handleChartHeightChange,
      rawFeaturesData,
      selectedFeatures,
      setSelectedFeatures,
      selectedFeaturesChartData,
      showDataTable
    ]
  );

  const renderDefaultContent = useCallback(
    () => (
      <>
        <FeatureControls
          rawFeatureData={rawFeaturesData[0]?.data || []}
          selectedFeatures={selectedFeatures}
          setSelectedFeatures={setSelectedFeatures}
          chartHeight={chartHeight}
          onChartHeightChange={handleChartHeightChange}
          showDataTable={showDataTable}
          onDataTableToggle={handleDataTableToggle}
        />
        <FeatureChart
          chartData={selectedFeaturesChartData[0] ?? {}}
          selectedFeatures={selectedFeatures}
          chartHeight={chartHeight}
          showDataTable={showDataTable}
        />
        <FeatureDataTable
          rawFeatureData={rawFeaturesData[0]?.data || []}
          selectedFeatures={selectedFeatures}
          showDataTable={showDataTable}
        />
      </>
    ),
    [
      chartHeight,
      handleChartHeightChange,
      handleDataTableToggle,
      rawFeaturesData,
      selectedFeatures,
      setSelectedFeatures,
      selectedFeaturesChartData,
      showDataTable
    ]
  );

  const renderContent = useCallback(() => {
    if (isLoading) return <LoadingState />;
    if (error) {
      logException("ExploreFeatures", "Feature Telemetry", "useFeatureData", error);
      return <ErrorState />;
    }
    if (hasNoData) {
      logEvent("Product Telemetry", "Customer Page", "ExploreFeatures", {
        ...telemetryPayload,
        eventName: "EmptyState"
      });
      return <EmptyState productName={productName} />;
    }
    return isIntune ? renderIntuneContent() : renderDefaultContent();
  }, [isLoading, error, hasNoData, isIntune, renderIntuneContent, renderDefaultContent, telemetryPayload, productName]);

  return (
    <ButtonWithPanel
      buttonProps={{
        buttonContent: "Explore Features"
      }}
      panelProps={{
        panelTitle: `Feature Explorer — ${productName}`,
        isLoading: false
      }}
      onPanelOpen={handlePanelOpen}
    >
      <Stack tokens={{ childrenGap: 15 }} style={{ padding: 20 }}>
        {renderContent()}
      </Stack>
    </ButtonWithPanel>
  );
};

export default ExploreFeatures;
